import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import debounce from 'lodash/debounce';

import { useField } from 'formik';
import type { SuggestionDataItem } from 'react-mentions';
import { MentionsInput, Mention } from 'react-mentions';

import Typography from 'src/components/ui/Typography';

import { colours } from 'src/constants/colours';
import { mentionRegEx } from 'src/constants/regexes';

import { useSearchUsersAndCompaniesWithTotalLazyQuery } from 'src/hooks/__generated__/queries';
import theme from 'src/utils/theme';
import { styledComponent, styledTag } from 'src/utils/styled';

const Container = styledTag('div')<{
    error: boolean;
    $isOnPostModal: boolean;
}>`
    margin: 1rem 0;
    width: 100%;
    position: relative;
    input:focus,
    textarea:focus {
        outline: none;
    }
    .comments-textarea__input {
        background-color: ${({ error }) => (error ? colours.redErrorBackground : colours.primary.O08)} !important;
        border-radius: 0.25rem;
        border: 0.0625rem solid
            ${({ error }) => (error ? theme.palette.error.main : colours.primary.O08)};
        padding: 1rem 0.5rem;
        font-size: 0.875rem !important;
        line-height: 1.5rem;
        color: ${theme.palette.text.primary};
        &:focus {
            border-color: ${colours.primary.O60};
        }
        &:invalid {
            border-color: ${theme.palette.error.main};
        }
        &::placeholder {
            opacity: 1;
        }
        &:-webkit-autofill {
            -webkit-box-shadow: 0 0 0 62.5rem ${colours.primary.O08} inset;
            border-radius: 0.25rem;
        }
    }
    .comments-textarea__suggestions {
        border-radius: 0.125rem;
    }
    .comments-textarea__suggestions__list {
        border: 0.0625rem solid ${colours.primary.O08};
        font-size: 0.875rem;
        max-height: 12rem;
        max-width: 15rem;
        overflow: auto;
        border-radius: 0.125rem;
    }
    .comments-textarea__suggestions__item {
        border: 0.0625rem solid ${colours.primary.O08};
    }
    .comments-textarea__suggestions__item--focused {
        background-color: ${colours.lightGray};
    }
    .comments-textarea__highlighter {
        padding: 1.0875rem 0.5rem;
        border: none !important;
        line-height: 1.5rem;
        font-size: 0.885rem;
        strong {
            z-index: 1;
            position: relative;
            color: ${colours.secondary.main};
            pointer-events: none;
            background-color: ${colours.primary.O08};
        }
    }
    ${({ $isOnPostModal }) =>
        $isOnPostModal &&
        `
   .comments-textarea__highlighter {
        padding: 0.54375rem 0.5rem;
    }
    .comments-textarea__input {
        padding: 0.45rem;
    }
   `};
`;

let containerForPortal: Element | undefined;

const StyledItem = styledTag('div')`
    padding: 0.5rem 1rem;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const StyledHelperText = styledComponent(Typography)`
    margin-top: 0.125rem;
    font-size: 0.75rem;
`;

type Props = {
    name: string; // Required
    rows?: number;
    isOnPostModal?: boolean;
    maxRows?: number;
    placeholder?: string;
    helperText?: string;
    hideErrors?: boolean;
    autoFocus?: boolean;
    showCountDown?: boolean;
    maxLength?: number;
    onFocus?: () => void;
    postId?: string;
};

export interface MentionTextFieldHandle {
    focus: () => void;
    blur: () => void;
}

const MentionTextField: React.ForwardRefRenderFunction<MentionTextFieldHandle, Props> = (
    {
        name,
        isOnPostModal,
        rows,
        maxRows,
        placeholder,
        helperText,
        hideErrors,
        autoFocus,
        showCountDown,
        maxLength,
        onFocus,
        postId,
    },
    ref,
) => {
    const [field, meta, helpers] = useField(name);
    const inputRef = useRef<HTMLInputElement>(null);
    const { setValue } = helpers;
    const [lastResultsEmpty, setLastResultsEmpty] = useState<boolean>(false);
    const [searchUsersAndCompaniesWithTotal] = useSearchUsersAndCompaniesWithTotalLazyQuery();

    useImperativeHandle(ref, () => ({
        focus: () => inputRef.current?.focus(),
        blur: () => inputRef.current?.blur(),
    }));

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.selectionStart = inputRef.current.value.length; // to move cursor at the end
            inputRef.current.selectionEnd = inputRef.current.value.length;
        } else if (autoFocus === false && inputRef.current) {
            inputRef.current.blur();
        }
    }, [autoFocus]);

    const handleChange = (event: { target: { value: string } }) => {
        if (event.target.value.length < ((field.value as string | undefined) ?? '').length) {
            // means if we remove letters to search again
            setLastResultsEmpty(false);
        }
        setValue(event.target.value);
    };

    let style = {};
    if (rows && rows > 1) {
        style = {
            input: {
                height: `calc(1.5rem * ${rows} + 2rem)`, // line-height = 1.5rem, padding = 1rem 0.5rem
            },
            highlighter: {
                height: `calc(1.5rem * ${rows} + 2rem)`,
            },
        };
    }
    if (maxRows && maxRows > 1) {
        style = {
            input: {
                maxHeight: `calc(1.5rem * ${maxRows} + 2rem)`, // line-height = 1.5rem, padding = 1rem 0.5rem
                overflow: 'auto',
            },
            highlighter: {
                maxHeight: `calc(1.5rem * ${maxRows} + 2rem)`,
                overflow: 'auto',
            },
        };
    }

    const hasError = Boolean(!hideErrors && meta.error);

    let helperTextToShow = helperText;

    if (showCountDown && maxLength) {
        const matches = [...(field.value as string).matchAll(mentionRegEx)];
        let transformedMessage = field.value as string;
        if (matches.length > 0) {
            matches.forEach(([toReplace, id, nameDisplay]) => {
                transformedMessage = transformedMessage.replace(toReplace, nameDisplay);
            });
        }
        helperTextToShow = `${transformedMessage.length} / ${maxLength}`;
    }

    const errorText = hasError ? meta.error : undefined;

    const textAtBottom = errorText ?? helperTextToShow;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchUsersAndCompanies = useCallback(
        debounce(async (query: string, callback: (data: SuggestionDataItem[]) => void) => {
            if (!lastResultsEmpty) {
                await searchUsersAndCompaniesWithTotal({
                    variables: {
                        offset: 0,
                        limit: 10,
                        search: query,
                    },
                    fetchPolicy: 'network-only',
                })
                    .then((usersAndCompanies) => {
                        // if result is empty we want to stop query the backend again and again
                        setLastResultsEmpty(
                            query.length > 0 &&
                                usersAndCompanies.data?.searchUsersAndCompaniesWithTotal?.results?.length === 0,
                        );
                        if (usersAndCompanies.data?.searchUsersAndCompaniesWithTotal?.results) {
                            return usersAndCompanies.data.searchUsersAndCompaniesWithTotal.results.map((item) => {
                                let id;
                                let display;
                                if (item.__typename === 'User') {
                                    id = item.id;
                                    display = item.fullName;
                                }
                                if (item.__typename === 'Company') {
                                    id = `comp:${item.id}`;
                                    display = item.name;
                                }
                                return {
                                    id: id ?? '',
                                    display: display ?? '',
                                };
                            });
                        }
                        return [];
                    })
                    .then(callback);
            }
        }, 500),
        [lastResultsEmpty],
    );

    return (
        <Container
            ref={(el) => {
                containerForPortal = el as Element | undefined;
            }}
            error={hasError}
            $isOnPostModal={isOnPostModal as boolean}
        >
            <MentionsInput
                data-target="mention-textfield-input"
                data-target-id="mention-textfield-input"
                value={field.value ? String(field.value) : ''}
                onChange={handleChange}
                className="comments-textarea"
                placeholder={placeholder}
                style={style}
                suggestionsPortalHost={containerForPortal}
                onFocus={onFocus}
                inputRef={inputRef}
                allowSpaceInQuery
            >
                <Mention
                    trigger="@"
                    data={fetchUsersAndCompanies}
                    markup="@@id@__id__@name@__display__@@"
                    renderSuggestion={(suggestion, _search, highlightedDisplay, index, focused) => (
                        <StyledItem>{highlightedDisplay}</StyledItem>
                    )}
                    appendSpaceOnAdd
                />
            </MentionsInput>
            {textAtBottom && (
                <StyledHelperText variant="body2" color={hasError ? 'error' : undefined}>
                    {textAtBottom}
                </StyledHelperText>
            )}
        </Container>
    );
};

export default forwardRef(MentionTextField);
